import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import styled, { keyframes } from '../../theme';
import { Modal } from '../../ui_components/modal';
import PageDimmer from '../../ui_components/dimmer/PageDimmer';
import { FiPlus, FiSend, FiThumbsDown, FiThumbsUp, FiX } from 'react-icons/fi';
import { checkGate } from '../../statsig';
import type { AssistantStreamCallbacks } from '../../api/ai';
import { QUIPA_ASSISTANT_FEEDBACK_GATE } from './quipa-assistant-events';
import {
  AssistantChatFeedback,
  AssistantChatMessage as PersistedAssistantChatMessage,
  AssistantChatSession,
  createAssistantChatFeedback,
  createAssistantChatSession,
  createAssistantChatMessage,
  listAssistantChatFeedbackAll,
  listAssistantChatFeedbackForAccount,
  listAssistantChatMessages,
  listAssistantChatMessagesForAccount,
  listAssistantChatSessions,
  reviewAssistantChatFeedback,
} from '../../api/assistant-chat';
import {
  AiInstruction,
  deleteAiInstruction,
  listAiInstructions,
  updateAiInstruction,
} from '../../api/assistant-instructions';

type Sender = 'assistant' | 'user';
type InstructionViewScope = 'all' | 'global' | 'account';
type NewInstructionScope = 'global' | 'account';

export interface AssistantMessage {
  id: string;
  sender: Sender;
  text: string;
  timestamp: string;
  metadata?: any;
  persistedId?: number;
  sessionId?: string;
}

type AssistantSendCallbacks = Pick<AssistantStreamCallbacks, 'onStatus' | 'onMessage'>;

const MIN_STATUS_DISPLAY_MS = 1600;
const FINALIZING_STATUS = 'Finalizing your reply...';
const FINALIZING_MIN_ELAPSED_MS = 9000;
const STREAM_MESSAGE_FLUSH_MS = 90;
const SYNTHETIC_STATUS_STEPS = [
  { text: 'Reviewing your request...', delayMs: 0 },
  { text: 'Matching client and room...', delayMs: 2600 },
  { text: 'Building your draft...', delayMs: 5600 },
  { text: FINALIZING_STATUS, delayMs: 9200 },
];

interface QuipaAssistantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend?: (
    message: string,
    sessionId: string,
    callbacks?: AssistantSendCallbacks,
    signal?: AbortSignal
  ) => Promise<string | AssistantMessage | void>;
  launchContext?: Record<string, unknown>;
}

const formatTime = () => moment().format('h:mm A');
const makeSessionId = () => `assistant-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
const resolveAccountId = (): number | null => {
  try {
    const rawStore = localStorage.getItem('quipa-store');
    const parsed = rawStore ? JSON.parse(rawStore) : null;
    const fromUser = parsed?.user?.accountId || parsed?.user?.account?.id;
    const stored = localStorage.getItem('accountId');
    const fromLocal = stored ? Number(stored) : undefined;
    const candidate = fromUser || fromLocal;
    return Number.isFinite(candidate as number) ? Number(candidate) : null;
  } catch (_err) {
    return null;
  }
};

const AssistantModalContainer = styled(Modal)`
  && {
    width: calc(100vw - 64px);
    max-width: 1360px;
    height: calc(100vh - 64px);
    max-height: 900px;
    padding: 0;
    margin: 1.25rem auto;
    overflow: hidden;
    border-radius: 14px;
    display: flex;
    flex-direction: column;
  }
`;

const AssistantShell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #f8fbfd;
  color: #1f2937;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
  gap: 8px;
`;

const HeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #6b7280;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
    color: #111827;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActionButton = styled.button<{ isActive?: boolean }>`
  border: 1px solid ${({ isActive }) => (isActive ? '#3b97b1' : '#e2e8f0')};
  background: ${({ isActive }) => (isActive ? '#e0f7ff' : '#ffffff')};
  color: #0f172a;
  padding: 8px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ isActive }) => (isActive ? '#cfefff' : '#f1f5f9')};
  }
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 9999px;
  background: #e0f2fe;
  color: #075985;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1px;
`;

const ChatSurface = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const Sidebar = styled.aside`
  width: 300px;
  max-width: 42%;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SidebarHeader = styled.div`
  padding: 14px 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const SidebarTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const NewChatButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const SessionsList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SessionButton = styled.button<{ isActive?: boolean }>`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${({ isActive }) => (isActive ? '#93c5fd' : 'transparent')};
  background: ${({ isActive }) => (isActive ? '#eff6ff' : 'transparent')};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${({ isActive }) => (isActive ? '#eff6ff' : '#f1f5f9')};
  }
`;

const SessionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionMeta = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #0f172a;
`;

const EmptyStateContent = styled.div`
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const EmptyStateTitle = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
`;

const EmptyStateSubtitle = styled.div`
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
`;

const pulse = keyframes`
  0% { transform: scale(0.85); opacity: 0.5; }
  50% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.85); opacity: 0.5; }
`;

const ThinkingBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 24px 12px;
  color: #475569;
  font-size: 13px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
`;

const Dot = styled.div<{ delay?: string }>`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #3b97b1;
  animation: ${pulse} 1.2s ease-in-out infinite;
  animation-delay: ${(p) => p.delay || '0s'};
`;

const MessageRow = styled.div<{ fromUser?: boolean }>`
  display: flex;
  justify-content: ${({ fromUser }) => (fromUser ? 'flex-end' : 'flex-start')};
`;

const MessageStack = styled.div<{ fromUser?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ fromUser }) => (fromUser ? 'flex-end' : 'flex-start')};
  gap: 6px;
`;

const MessageBubble = styled.div<{ fromUser?: boolean; isHighlighted?: boolean }>`
  background: ${({ fromUser }) => (fromUser ? '#e0f7ff' : '#f3f4f6')};
  color: #111827;
  max-width: 820px;
  padding: 14px 16px;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(18, 38, 63, 0.08);
  border: 1px solid
    ${({ fromUser, isHighlighted }) =>
      isHighlighted ? '#3b97b1' : fromUser ? '#bae6fd' : '#e5e7eb'};
  word-break: break-word;

  ${({ isHighlighted }) =>
    isHighlighted ? 'box-shadow: 0 0 0 2px rgba(59, 151, 177, 0.25);' : ''}
`;

const PlainText = styled.div`
  white-space: pre-wrap;
`;

const MarkdownContainer = styled.div`
  font-size: 14px;
  line-height: 1.55;

  p {
    margin: 0;
  }

  p + p {
    margin-top: 10px;
  }

  ul,
  ol {
    margin: 8px 0 0 18px;
    padding: 0;
  }

  li {
    margin: 4px 0;
  }

  h1,
  h2,
  h3,
  h4 {
    margin: 0;
    font-weight: 700;
    color: #0f172a;
  }

  h3,
  h4 {
    font-size: 14px;
  }

  h1,
  h2 {
    font-size: 15px;
  }
`;

const InlineCode = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 0.95em;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(15, 23, 42, 0.1);
`;

const CodeBlock = styled.pre`
  margin: 10px 0 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: #0b1220;
  color: #e2e8f0;
  overflow-x: auto;

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
      'Courier New', monospace;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre;
  }
`;

const MessageActions = styled.div<{ fromUser?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #64748b;
  justify-content: ${({ fromUser }) => (fromUser ? 'flex-end' : 'flex-start')};
`;

const FeedbackLinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;

  &:hover {
    color: #1d4ed8;
  }

  &:disabled {
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const FeedbackPanel = styled.div`
  width: min(560px, 100%);
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const FeedbackLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
`;

const FeedbackRatingGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const FeedbackRatingButton = styled.button<{ active?: boolean; intent?: 'good' | 'bad' }>`
  border: 1px solid ${({ active }) => (active ? '#3b97b1' : '#d1d5db')};
  background: ${({ active }) => (active ? '#e0f7ff' : '#ffffff')};
  color: ${({ intent }) => (intent === 'bad' ? '#b91c1c' : '#0f172a')};
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: ${({ active }) => (active ? '#cfefff' : '#f1f5f9')};
  }
`;

const FeedbackTextarea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #0f172a;
  font-size: 13px;
  line-height: 1.4;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b97b1;
    box-shadow: 0 0 0 3px rgba(59, 151, 177, 0.15);
  }
`;

const FeedbackActions = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const FeedbackButtons = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const FeedbackStatus = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const FeedbackGhostButton = styled.button`
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #334155;
  }
`;

const FeedbackSubmitButton = styled.button`
  border: 1px solid #d1d5db;
  background: #3b97b1;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: #2f7f95;
  }

  &:disabled {
    background: #9ca3af;
    border-color: #d1d5db;
    cursor: not-allowed;
  }
`;

const FeedbackCountButton = styled.button`
  border: none;
  background: transparent;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: #0f172a;
  }
`;

const FeedbackInlinePanel = styled.div`
  width: min(560px, 100%);
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FeedbackInlineHeader = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
`;

const FeedbackInlineItem = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 10px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FeedbackInlineMeta = styled.div`
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
`;

const FeedbackInlineComment = styled.div`
  font-size: 13px;
  color: #0f172a;
  white-space: pre-wrap;
  line-height: 1.45;
`;

const SessionFeedbackBanner = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 12px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Timestamp = styled.div<{ fromUser?: boolean }>`
  margin-top: 6px;
  font-size: 12px;
  color: #9ca3af;
  text-align: ${({ fromUser }) => (fromUser ? 'right' : 'left')};
`;

const Composer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px 18px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  position: sticky;
  bottom: 0;
`;

const ComposerRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const PromptInput = styled.textarea`
  flex: 1;
  min-height: 64px;
  max-height: 220px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  resize: none;
  line-height: 1.4;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b97b1;
    box-shadow: 0 0 0 3px rgba(59, 151, 177, 0.15);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid #d1d5db;
  background: #3b97b1;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.1s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    background: #2f7f95;
    box-shadow: 0 8px 18px rgba(59, 151, 177, 0.25);
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    background: #9ca3af;
    border-color: #d1d5db;
    cursor: not-allowed;
    box-shadow: none;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ShortcutHint = styled.div`
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
`;

const SessionFeedbackSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
`;

const SessionFeedbackToggle = styled.button`
  align-self: center;
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: #1d4ed8;
  }
`;

const ReviewContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

const ReviewHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ReviewTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const ReviewSubtitle = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const ReviewList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ReviewCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ReviewTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ReviewTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #0f172a;
  font-size: 11px;
  font-weight: 600;
`;

const ReviewComment = styled.div`
  font-size: 14px;
  color: #0f172a;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const ReviewField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ReviewFieldLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const ReviewTextarea = styled(FeedbackTextarea)`
  min-height: 64px;
  font-size: 12px;
`;

const ReviewExcerpt = styled.div`
  font-size: 12px;
  color: #475569;
  background: #ffffff;
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  white-space: pre-wrap;
`;

const ReviewMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const ReviewActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ReviewButtons = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const ReviewButton = styled.button<{ active?: boolean; intent?: 'up' | 'down' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  border: 1px solid ${({ active }) => (active ? '#3b97b1' : '#d1d5db')};
  background: ${({ active }) => (active ? '#e0f7ff' : '#ffffff')};
  color: ${({ intent }) => (intent === 'down' ? '#b91c1c' : '#0f172a')};
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;

  &:hover {
    background: ${({ active }) => (active ? '#cfefff' : '#f1f5f9')};
  }

  &:disabled {
    cursor: not-allowed;
    color: #94a3b8;
    border-color: #e2e8f0;
  }
`;

const ReviewOpenRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ReviewOpenButton = styled.button`
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #0f172a;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const ReviewStatus = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const InstructionsContainer = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

const InstructionsHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InstructionsTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
`;

const InstructionsSubtitle = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const InstructionsBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const InstructionsForm = styled.form`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InstructionsField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InstructionsLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const InstructionsTextarea = styled(FeedbackTextarea)`
  min-height: 120px;
  font-size: 13px;
`;

const InstructionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const InstructionsSelect = styled.select`
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #ffffff;
  color: #0f172a;
  font-size: 12px;
  padding: 6px 10px;
`;

const InstructionsHint = styled.div`
  font-size: 11px;
  color: #94a3b8;
`;

const InstructionsActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const InstructionsButtons = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const InstructionsGhostButton = styled.button`
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #334155;
  }
`;

const InstructionsSubmitButton = styled.button`
  border: 1px solid #d1d5db;
  background: #3b97b1;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: #2f7f95;
  }

  &:disabled {
    background: #9ca3af;
    border-color: #d1d5db;
    cursor: not-allowed;
  }
`;

const InstructionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InstructionCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InstructionText = styled.div`
  font-size: 14px;
  color: #0f172a;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const InstructionMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 11px;
  color: #64748b;
`;

const InstructionMetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #0f172a;
  font-size: 11px;
  font-weight: 600;
`;

const InstructionEditTextarea = styled(InstructionsTextarea)`
  min-height: 90px;
`;

const InstructionCardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const InstructionActionButton = styled.button<{ intent?: 'primary' | 'danger' | 'ghost' }>`
  border: 1px solid
    ${({ intent }) =>
      intent === 'danger' ? '#ef4444' : intent === 'primary' ? '#3b97b1' : '#d1d5db'};
  background: ${({ intent }) => (intent === 'primary' ? '#3b97b1' : '#ffffff')};
  color: ${({ intent }) => (intent === 'primary' ? '#ffffff' : intent === 'danger' ? '#b91c1c' : '#0f172a')};
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: ${({ intent }) =>
      intent === 'primary' ? '#2f7f95' : intent === 'danger' ? '#fef2f2' : '#f1f5f9'};
  }

  &:disabled {
    cursor: not-allowed;
    color: #94a3b8;
    border-color: #e2e8f0;
    background: #f8fafc;
  }
`;

const InstructionActionStatus = styled.div`
  font-size: 12px;
  color: #64748b;
`;

type MarkdownBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; level: number; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'code'; language?: string; code: string };

const splitByCodeBlocks = (input: string): MarkdownBlock[] => {
  if (!input) return [];

  const blocks: MarkdownBlock[] = [];
  let cursor = 0;

  while (cursor < input.length) {
    const start = input.indexOf('```', cursor);
    if (start === -1) {
      blocks.push({ type: 'paragraph', text: input.slice(cursor) });
      break;
    }

    if (start > cursor) {
      blocks.push({ type: 'paragraph', text: input.slice(cursor, start) });
    }

    const end = input.indexOf('```', start + 3);
    if (end === -1) {
      blocks.push({ type: 'paragraph', text: input.slice(start) });
      break;
    }

    const raw = input.slice(start + 3, end);
    const firstNewline = raw.indexOf('\n');
    const language = firstNewline === -1 ? raw.trim() : raw.slice(0, firstNewline).trim();
    const code = firstNewline === -1 ? '' : raw.slice(firstNewline + 1);

    blocks.push({
      type: 'code',
      language: language || undefined,
      code: code.replace(/\n$/, ''),
    });

    cursor = end + 3;
  }

  return blocks;
};

const parseTextBlocks = (input: string): MarkdownBlock[] => {
  const lines = (input || '').split('\n');
  const blocks: MarkdownBlock[] = [];

  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join('\n').trimEnd();
    if (text.trim()) blocks.push({ type: 'paragraph', text });
    paragraph = [];
  };

  const flushList = () => {
    if (!list || list.items.length === 0) return;
    blocks.push({ type: 'list', ordered: list.ordered, items: list.items });
    list = null;
  };

  for (const line of lines) {
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2].trim(),
      });
      continue;
    }

    const ulMatch = line.match(/^\s*[-*+]\s+(.*)$/);
    const olMatch = line.match(/^\s*\d+\.\s+(.*)$/);
    const listMatch = ulMatch || olMatch;
    if (listMatch) {
      flushParagraph();
      const ordered = Boolean(olMatch);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push(listMatch[1].trim());
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return blocks;
};

const parseMarkdownBlocks = (input: string): MarkdownBlock[] => {
  const withCode = splitByCodeBlocks(input);
  const blocks: MarkdownBlock[] = [];

  for (const block of withCode) {
    if (block.type === 'code') {
      blocks.push(block);
      continue;
    }

    const text = block.text;
    blocks.push(...parseTextBlocks(text));
  }

  return blocks;
};

const parseInline = (input: string, keyPrefix: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  let text = input || '';
  let idx = 0;

  const patterns: Array<{
    type: 'code' | 'boldItalic' | 'bold' | 'italic';
    re: RegExp;
  }> = [
    { type: 'code', re: /`([^`]+)`/ },
    { type: 'boldItalic', re: /\*\*\*([^*]+)\*\*\*/ },
    { type: 'bold', re: /\*\*([^*]+)\*\*/ },
    { type: 'italic', re: /\*([^*]+)\*/ },
  ];

  while (text) {
    let best: { match: RegExpExecArray; type: (typeof patterns)[number]['type'] } | null = null;

    for (const p of patterns) {
      const m = p.re.exec(text);
      if (!m) continue;
      if (!best || m.index < best.match.index) {
        best = { match: m, type: p.type };
      }
    }

    if (!best) {
      out.push(text);
      break;
    }

    if (best.match.index > 0) {
      out.push(text.slice(0, best.match.index));
    }

    const content = best.match[1];
    const key = `${keyPrefix}-${idx++}`;

    if (best.type === 'code') {
      out.push(<InlineCode key={key}>{content}</InlineCode>);
    } else if (best.type === 'boldItalic') {
      out.push(
        <strong key={key}>
          <em>{content}</em>
        </strong>
      );
    } else if (best.type === 'bold') {
      out.push(<strong key={key}>{content}</strong>);
    } else {
      out.push(<em key={key}>{content}</em>);
    }

    text = text.slice(best.match.index + best.match[0].length);
  }

  return out;
};

const renderInlineWithBreaks = (input: string, keyPrefix: string): React.ReactNode[] => {
  const lines = (input || '').split('\n');
  const nodes: React.ReactNode[] = [];

  lines.forEach((line, lineIndex) => {
    nodes.push(...parseInline(line, `${keyPrefix}-l${lineIndex}`));
    if (lineIndex < lines.length - 1) {
      nodes.push(<br key={`${keyPrefix}-br${lineIndex}`} />);
    }
  });

  return nodes;
};

const AssistantMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const blocks = useMemo(() => parseMarkdownBlocks(text), [text]);

  return (
    <MarkdownContainer>
      {blocks.map((block, index) => {
        if (block.type === 'code') {
          return (
            <CodeBlock key={`b-${index}`}>
              <code>{block.code}</code>
            </CodeBlock>
          );
        }

        if (block.type === 'heading') {
          const level = Math.min(Math.max(block.level, 1), 4);
          const Tag = `h${level}` as keyof JSX.IntrinsicElements;
          return <Tag key={`b-${index}`}>{parseInline(block.text, `h-${index}`)}</Tag>;
        }

        if (block.type === 'list') {
          const ListTag = block.ordered ? 'ol' : 'ul';
          return (
            <ListTag key={`b-${index}`}>
              {block.items.map((item, itemIndex) => (
                <li key={`li-${index}-${itemIndex}`}>
                  {renderInlineWithBreaks(item, `li-${index}-${itemIndex}`)}
                </li>
              ))}
            </ListTag>
          );
        }

        return <p key={`b-${index}`}>{renderInlineWithBreaks(block.text, `p-${index}`)}</p>;
      })}
    </MarkdownContainer>
  );
};

const mapPersistedMessage = (message: PersistedAssistantChatMessage): AssistantMessage => ({
  id: `assistant-chat-message-${message.id}`,
  sender: message.sender,
  text: message.text,
  timestamp: message.createdAt ? moment(message.createdAt).format('h:mm A') : formatTime(),
  metadata: message.metadata ?? null,
  persistedId: message.id,
  sessionId: message.sessionId,
});

const normalizePositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const resolveClientIdFromContext = (context?: Record<string, unknown>): number | null => {
  if (!context || typeof context !== 'object') return null;

  const asRecord = context as Record<string, unknown>;
  const nestedClient =
    asRecord.client && typeof asRecord.client === 'object'
      ? (asRecord.client as Record<string, unknown>)
      : null;
  const nestedProject =
    asRecord.project && typeof asRecord.project === 'object'
      ? (asRecord.project as Record<string, unknown>)
      : null;

  const candidates = [
    asRecord.clientId,
    asRecord.projectClientId,
    asRecord.selectedClientId,
    nestedClient?.id,
    nestedProject?.clientId,
  ];

  for (const candidate of candidates) {
    const normalized = normalizePositiveNumber(candidate);
    if (normalized) return normalized;
  }

  return null;
};

const QuipaAssistantModal: React.FC<QuipaAssistantModalProps> = ({
  isOpen,
  onClose,
  onSend,
  launchContext,
}) => {
  const [sessions, setSessions] = useState<AssistantChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string>(makeSessionId());
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [draft, setDraft] = useState<string>('');
  const [assistantStatus, setAssistantStatus] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isStreamingAssistantMessage, setIsStreamingAssistantMessage] = useState<boolean>(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState<boolean>(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  const [messageFeedbackOpen, setMessageFeedbackOpen] = useState<Record<string, boolean>>({});
  const [messageFeedbackDrafts, setMessageFeedbackDrafts] = useState<Record<string, string>>({});
  const [messageFeedbackStatus, setMessageFeedbackStatus] = useState<Record<string, string>>({});
  const [messageFeedbackRatings, setMessageFeedbackRatings] = useState<
    Record<string, boolean | null>
  >({});
  const [sessionFeedbackOpen, setSessionFeedbackOpen] = useState<boolean>(false);
  const [sessionFeedbackDraft, setSessionFeedbackDraft] = useState<string>('');
  const [sessionFeedbackStatus, setSessionFeedbackStatus] = useState<string>('');
  const [sessionFeedbackRating, setSessionFeedbackRating] = useState<boolean | null>(null);
  const [feedbackSendingFor, setFeedbackSendingFor] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'chat' | 'review' | 'instructions'>('chat');
  const [instructionFilterScope, setInstructionFilterScope] = useState<InstructionViewScope>('all');
  const [instructionScope, setInstructionScope] = useState<NewInstructionScope>('global');
  const [instructionDraft, setInstructionDraft] = useState<string>('');
  const [instructionSaveStatus, setInstructionSaveStatus] = useState<string>('');
  const [isSavingInstruction, setIsSavingInstruction] = useState<boolean>(false);
  const [instructionActionStatus, setInstructionActionStatus] = useState<Record<number, string>>({});
  const [editingInstructionId, setEditingInstructionId] = useState<number | null>(null);
  const [instructionEditDraft, setInstructionEditDraft] = useState<string>('');
  const [isUpdatingInstructionId, setIsUpdatingInstructionId] = useState<number | null>(null);
  const [isDeletingInstructionId, setIsDeletingInstructionId] = useState<number | null>(null);
  const [instructions, setInstructions] = useState<AiInstruction[]>([]);
  const [isLoadingInstructions, setIsLoadingInstructions] = useState<boolean>(false);
  const [reviewFeedbackItems, setReviewFeedbackItems] = useState<AssistantChatFeedback[]>([]);
  const [isLoadingReviewFeedback, setIsLoadingReviewFeedback] = useState<boolean>(false);
  const [reviewActionStatus, setReviewActionStatus] = useState<Record<number, string>>({});
  const [reviewCommentDrafts, setReviewCommentDrafts] = useState<Record<number, string>>({});
  const [sessionFeedbackItems, setSessionFeedbackItems] = useState<AssistantChatFeedback[]>([]);
  const [isLoadingSessionFeedback, setIsLoadingSessionFeedback] = useState<boolean>(false);
  const [sessionFeedbackViewOpen, setSessionFeedbackViewOpen] = useState<boolean>(false);
  const [messageFeedbackViewOpen, setMessageFeedbackViewOpen] = useState<
    Record<number, boolean>
  >({});
  const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
  const [messageLoadScope, setMessageLoadScope] = useState<'user' | 'account'>('user');
  const canReviewFeedback = checkGate(QUIPA_ASSISTANT_FEEDBACK_GATE);
  const canManageInstructions = canReviewFeedback;
  const contextualClientId = useMemo(
    () => resolveClientIdFromContext(launchContext),
    [launchContext]
  );
  const filterScopeAccountId =
    instructionFilterScope === 'account' ? resolveAccountId() : null;
  const instructionAccountId = instructionScope === 'account' ? resolveAccountId() : null;
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastLoadedSessionIdRef = useRef<string | null>(null);
  const pendingHighlightIdRef = useRef<number | null>(null);
  const sendAbortRef = useRef<AbortController | null>(null);
  const statusTimeoutRef = useRef<number | null>(null);
  const syntheticStatusTimerRefs = useRef<number[]>([]);
  const streamedMessageFlushRef = useRef<number | null>(null);
  const pendingStreamedMessageRef = useRef<string>('');
  const pendingStatusesRef = useRef<string[]>([]);
  const activeStatusRef = useRef<string>('');
  const lastStatusAtRef = useRef<number>(0);
  const sendStartedAtRef = useRef<number>(0);

  const clearPendingStatusTimeout = () => {
    if (statusTimeoutRef.current != null) {
      window.clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = null;
    }
  };

  const clearSyntheticStatusTimers = () => {
    syntheticStatusTimerRefs.current.forEach((timerId) => window.clearTimeout(timerId));
    syntheticStatusTimerRefs.current = [];
  };

  const clearStreamedMessageFlush = () => {
    if (streamedMessageFlushRef.current != null) {
      window.clearTimeout(streamedMessageFlushRef.current);
      streamedMessageFlushRef.current = null;
    }
  };

  const resetStatusDisplay = () => {
    clearPendingStatusTimeout();
    clearSyntheticStatusTimers();
    clearStreamedMessageFlush();
    pendingStreamedMessageRef.current = '';
    pendingStatusesRef.current = [];
    activeStatusRef.current = '';
    lastStatusAtRef.current = 0;
    sendStartedAtRef.current = 0;
    setAssistantStatus('');
  };

  const applyDisplayedStatus = (status: string) => {
    activeStatusRef.current = status;
    lastStatusAtRef.current = Date.now();
    setAssistantStatus(status);
    setIsStreamingAssistantMessage(false);
  };

  const flushNextQueuedStatus = () => {
    if (statusTimeoutRef.current != null) return;
    const nextStatus = pendingStatusesRef.current[0];
    if (!nextStatus) return;

    const now = Date.now();
    const elapsedSinceLastStatus = lastStatusAtRef.current
      ? now - lastStatusAtRef.current
      : MIN_STATUS_DISPLAY_MS;
    const minimumGap = activeStatusRef.current
      ? Math.max(0, MIN_STATUS_DISPLAY_MS - elapsedSinceLastStatus)
      : 0;
    const finalizingDelay =
      nextStatus === FINALIZING_STATUS && sendStartedAtRef.current
        ? Math.max(0, FINALIZING_MIN_ELAPSED_MS - (now - sendStartedAtRef.current))
        : 0;
    const delay = Math.max(minimumGap, finalizingDelay);

    statusTimeoutRef.current = window.setTimeout(() => {
      statusTimeoutRef.current = null;
      const queuedStatus = pendingStatusesRef.current.shift();
      if (!queuedStatus) return;
      applyDisplayedStatus(queuedStatus);
      flushNextQueuedStatus();
    }, delay);
  };

  const queueDisplayedStatus = (status: string | null) => {
    const normalized = String(status || '').trim();
    if (!normalized) return;

    const lastQueued = pendingStatusesRef.current[pendingStatusesRef.current.length - 1];
    if (normalized === activeStatusRef.current || normalized === lastQueued) return;

    if (!activeStatusRef.current && pendingStatusesRef.current.length === 0) {
      if (
        normalized === FINALIZING_STATUS &&
        sendStartedAtRef.current &&
        Date.now() - sendStartedAtRef.current < FINALIZING_MIN_ELAPSED_MS
      ) {
        pendingStatusesRef.current.push(normalized);
        flushNextQueuedStatus();
        return;
      }

      applyDisplayedStatus(normalized);
      return;
    }

    pendingStatusesRef.current.push(normalized);
    flushNextQueuedStatus();
  };

  const startSyntheticStatusSequence = () => {
    clearSyntheticStatusTimers();
    if (!SYNTHETIC_STATUS_STEPS.length) return;

    const [firstStep, ...remainingSteps] = SYNTHETIC_STATUS_STEPS;
    applyDisplayedStatus(firstStep.text);

    remainingSteps.forEach((step) => {
      const timerId = window.setTimeout(() => {
        queueDisplayedStatus(step.text);
      }, step.delayMs);
      syntheticStatusTimerRefs.current.push(timerId);
    });
  };

  const cancelActiveSend = () => {
    if (sendAbortRef.current) {
      sendAbortRef.current.abort();
      sendAbortRef.current = null;
    }
    resetStatusDisplay();
    setIsStreamingAssistantMessage(false);
    setIsSending(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadSessions = async () => {
      setIsLoadingSessions(true);
      const startNewSession = () => {
        const newSessionId = makeSessionId();
        lastLoadedSessionIdRef.current = null;
        pendingHighlightIdRef.current = null;
        setMessageLoadScope('user');
        setHighlightedMessageId(null);
        setActiveSessionId(newSessionId);
        setMessages([]);
        setIsLoadingMessages(false);
      };

      try {
        setMessages([]);
        const loaded = await listAssistantChatSessions();
        if (cancelled) return;
        setSessions(loaded);
        startNewSession();
      } catch (error) {
        console.error('Failed to load assistant chat sessions', error);
        if (cancelled) return;
        setSessions([]);
        startNewSession();
      } finally {
        if (!cancelled) setIsLoadingSessions(false);
      }
    };

    setDraft('');
    resetStatusDisplay();
    setIsSending(false);
    setIsStreamingAssistantMessage(false);
    loadSessions();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    cancelActiveSend();
  }, [isOpen]);

  useEffect(() => () => {
    clearPendingStatusTimeout();
    clearSyntheticStatusTimers();
    clearStreamedMessageFlush();
    if (sendAbortRef.current) {
      sendAbortRef.current.abort();
      sendAbortRef.current = null;
    }
  }, []);

  const sessionLevelFeedback = useMemo(
    () => sessionFeedbackItems.filter((item) => !item.messageId),
    [sessionFeedbackItems]
  );

  const messageFeedbackMap = useMemo(() => {
    const map = new Map<number, AssistantChatFeedback[]>();
    sessionFeedbackItems.forEach((item) => {
      if (!item.messageId) return;
      const id = Number(item.messageId);
      const group = map.get(id) || [];
      group.push(item);
      map.set(id, group);
    });
    return map;
  }, [sessionFeedbackItems]);

  useEffect(() => {
    setMessageFeedbackOpen({});
    setMessageFeedbackDrafts({});
    setMessageFeedbackStatus({});
    setMessageFeedbackRatings({});
    setMessageFeedbackViewOpen({});
    setSessionFeedbackOpen(false);
    setSessionFeedbackDraft('');
    setSessionFeedbackStatus('');
    setSessionFeedbackRating(null);
    setSessionFeedbackItems([]);
    setSessionFeedbackViewOpen(false);
    setHighlightedMessageId(pendingHighlightIdRef.current);
    pendingHighlightIdRef.current = null;
    setViewMode('chat');
    resetStatusDisplay();
    setIsStreamingAssistantMessage(false);
  }, [activeSessionId]);

  useEffect(() => {
    if (!isOpen) return;
    const sessionExists = sessions.some((s) => s.sessionId === activeSessionId);
    if (!sessionExists && messageLoadScope !== 'account') return;
    if (lastLoadedSessionIdRef.current === activeSessionId) return;

    let cancelled = false;
    const loadMessages = async () => {
      setIsLoadingMessages(true);
      setMessages([]);
      try {
        lastLoadedSessionIdRef.current = activeSessionId;
        const loaded =
          messageLoadScope === 'account'
            ? await listAssistantChatMessagesForAccount(activeSessionId)
            : await listAssistantChatMessages(activeSessionId);
        if (cancelled) return;
        setMessages(loaded.map(mapPersistedMessage));
      } catch (error) {
        console.error('Failed to load assistant chat messages', error);
        if (cancelled) return;
        setMessages([]);
      } finally {
        if (!cancelled) setIsLoadingMessages(false);
      }
    };

    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [activeSessionId, isOpen, messageLoadScope, sessions]);

  useEffect(() => {
    if (!listRef.current) return;
    if (highlightedMessageId) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isOpen, highlightedMessageId]);

  useEffect(() => {
    if (!highlightedMessageId) return;
    if (!listRef.current) return;
    const target = listRef.current.querySelector(
      `[data-message-id="${highlightedMessageId}"]`
    ) as HTMLElement | null;
    if (target) {
      target.scrollIntoView({ block: 'center' });
    }
  }, [highlightedMessageId, messages]);

  useEffect(() => {
    if (!highlightedMessageId) return;
    setMessageFeedbackViewOpen((prev) => ({
      ...prev,
      [highlightedMessageId]: true,
    }));
  }, [highlightedMessageId]);

  useEffect(() => {
    if (!isOpen) return;
    if (viewMode !== 'review') return;
    if (!canReviewFeedback) return;

    let cancelled = false;

    const loadFeedback = async () => {
      setIsLoadingReviewFeedback(true);
      try {
        const feedback = await listAssistantChatFeedbackAll();
        if (cancelled) return;
        setReviewFeedbackItems(feedback);
      } catch (error) {
        console.error('Failed to load feedback review list', error);
        if (cancelled) return;
        setReviewFeedbackItems([]);
      } finally {
        if (!cancelled) setIsLoadingReviewFeedback(false);
      }
    };

    loadFeedback();

    return () => {
      cancelled = true;
    };
  }, [canReviewFeedback, isOpen, viewMode]);

  useEffect(() => {
    if (!isOpen) return;
    if (viewMode !== 'instructions') return;
    if (!canManageInstructions) return;

    let cancelled = false;

    const loadInstructions = async () => {
      setIsLoadingInstructions(true);
      setInstructionSaveStatus('');
      try {
        const scope = instructionFilterScope;
        let loaded: AiInstruction[] = [];

        if (scope === 'all') {
          loaded = await listAiInstructions({
            allAccounts: true,
            limit: 300,
          });
        } else {
          if (scope === 'account' && !filterScopeAccountId) {
            setInstructions([]);
            setInstructionSaveStatus('Account not found for scoped instructions.');
            return;
          }

          loaded = await listAiInstructions({
            scope,
            accountId: filterScopeAccountId || undefined,
            limit: 200,
          });
        }

        if (cancelled) return;
        const sorted = loaded.slice().sort((a, b) => {
          const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bTime - aTime;
        });
        setInstructions(sorted);
      } catch (error) {
        console.error('Failed to load instructions', error);
        if (cancelled) return;
        setInstructions([]);
        setInstructionSaveStatus('Unable to load instructions.');
      } finally {
        if (!cancelled) setIsLoadingInstructions(false);
      }
    };

    loadInstructions();

    return () => {
      cancelled = true;
    };
  }, [
    canManageInstructions,
    filterScopeAccountId,
    instructionFilterScope,
    isOpen,
    viewMode,
  ]);

  useEffect(() => {
    if (reviewFeedbackItems.length === 0) return;
    setReviewCommentDrafts((prev) => {
      const next = { ...prev };
      reviewFeedbackItems.forEach((item) => {
        if (next[item.id] === undefined) {
          next[item.id] = item.reviewComment || '';
        }
      });
      return next;
    });
  }, [reviewFeedbackItems]);

  useEffect(() => {
    if (!isOpen) return;
    if (viewMode !== 'chat') return;
    if (!activeSessionId) return;

    let cancelled = false;

    const loadSessionFeedback = async () => {
      setIsLoadingSessionFeedback(true);
      try {
        const feedback =
          messageLoadScope === 'account'
            ? await listAssistantChatFeedbackForAccount(activeSessionId)
            : await listAssistantChatFeedback(activeSessionId);
        if (cancelled) return;
        setSessionFeedbackItems(feedback);
      } catch (error) {
        console.error('Failed to load session feedback', error);
        if (cancelled) return;
        setSessionFeedbackItems([]);
      } finally {
        if (!cancelled) setIsLoadingSessionFeedback(false);
      }
    };

    loadSessionFeedback();

    return () => {
      cancelled = true;
    };
  }, [activeSessionId, isOpen, messageLoadScope, viewMode]);

  const adjustInputHeight = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  };

  useEffect(() => {
    adjustInputHeight();
  }, [draft]);

  const upsertSession = (session: AssistantChatSession) => {
    if (!session || !session.sessionId) return;
    setSessions((prev) => {
      const next = prev.slice();
      const index = next.findIndex((s) => s.sessionId === session.sessionId);
      if (index === -1) {
        next.unshift(session);
      } else {
        next[index] = { ...next[index], ...session };
      }

      next.sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      return next;
    });
  };

  const handleNewChat = () => {
    cancelActiveSend();
    const newSessionId = makeSessionId();
    lastLoadedSessionIdRef.current = null;
    setMessageLoadScope('user');
    setHighlightedMessageId(null);
    setActiveSessionId(newSessionId);
    setMessages([]);
    setDraft('');
  };

  const handleSelectSession = (session: AssistantChatSession) => {
    cancelActiveSend();
    setMessageLoadScope('user');
    setHighlightedMessageId(null);
    lastLoadedSessionIdRef.current = null;
    setActiveSessionId(session.sessionId);
  };

  const saveMessageBestEffort = async (
    sender: 'user' | 'assistant',
    text: string,
    localId: string,
    sessionId: string,
    metadata: any = null
  ) => {
    try {
      const { session, message } = await createAssistantChatMessage({
        sessionId,
        sender,
        text,
        metadata,
      });
      if (message && message.id) {
        setMessages((prev) =>
          prev.map((item) =>
            item.id === localId
              ? {
                  ...item,
                  persistedId: message.id,
                  sessionId: message.sessionId,
                }
              : item
          )
        );
      }
      if (session) upsertSession(session);
    } catch (error) {
      console.warn('Failed to persist assistant chat message', error);
    }
  };

  const toggleMessageFeedback = (messageId: string) => {
    setMessageFeedbackOpen((prev) => ({ ...prev, [messageId]: !prev[messageId] }));
    setMessageFeedbackStatus((prev) => ({ ...prev, [messageId]: '' }));
  };

  const handleSubmitMessageFeedback = async (message: AssistantMessage) => {
    const key = message.id;
    const comment = (messageFeedbackDrafts[key] || '').trim();
    const isHelpful = messageFeedbackRatings[key];

    if (!comment) {
      setMessageFeedbackStatus((prev) => ({ ...prev, [key]: 'Please enter feedback.' }));
      return;
    }

    if (typeof isHelpful !== 'boolean') {
      setMessageFeedbackStatus((prev) => ({
        ...prev,
        [key]: 'Select Helpful or Not Helpful.',
      }));
      return;
    }

    if (!message.persistedId) {
      setMessageFeedbackStatus((prev) => ({ ...prev, [key]: 'Message is still saving.' }));
      return;
    }

    const sessionId = message.sessionId || activeSessionId;
    if (!sessionId) {
      setMessageFeedbackStatus((prev) => ({ ...prev, [key]: 'Session not available.' }));
      return;
    }

    try {
      setFeedbackSendingFor(key);
      setMessageFeedbackStatus((prev) => ({ ...prev, [key]: 'Sending feedback...' }));
      const saved = await createAssistantChatFeedback({
        sessionId,
        messageId: message.persistedId,
        comment,
        isHelpful,
        clientId: contextualClientId || undefined,
      });
      setSessionFeedbackItems((prev) => [...prev, saved]);
      setMessageFeedbackStatus((prev) => ({ ...prev, [key]: 'Feedback saved.' }));
      setMessageFeedbackDrafts((prev) => ({ ...prev, [key]: '' }));
      setMessageFeedbackRatings((prev) => ({ ...prev, [key]: null }));
      setMessageFeedbackOpen((prev) => ({ ...prev, [key]: false }));
    } catch (error) {
      console.error('Failed to save message feedback', error);
      setMessageFeedbackStatus((prev) => ({
        ...prev,
        [key]: 'Unable to save feedback right now.',
      }));
    } finally {
      setFeedbackSendingFor(null);
    }
  };

  const handleSubmitSessionFeedback = async () => {
    const comment = sessionFeedbackDraft.trim();
    const isHelpful = sessionFeedbackRating;
    if (!comment) {
      setSessionFeedbackStatus('Please enter feedback.');
      return;
    }

    if (typeof isHelpful !== 'boolean') {
      setSessionFeedbackStatus('Select Helpful or Not Helpful.');
      return;
    }

    try {
      setFeedbackSendingFor('session');
      setSessionFeedbackStatus('Sending feedback...');
      const session = await createAssistantChatSession({
        sessionId: activeSessionId,
        context: launchContext || null,
      });
      if (session) upsertSession(session);
      const saved = await createAssistantChatFeedback({
        sessionId: activeSessionId,
        comment,
        isHelpful,
        clientId: contextualClientId || undefined,
      });
      setSessionFeedbackItems((prev) => [...prev, saved]);
      setSessionFeedbackStatus('Feedback saved.');
      setSessionFeedbackDraft('');
      setSessionFeedbackRating(null);
      setSessionFeedbackOpen(false);
    } catch (error) {
      console.error('Failed to save session feedback', error);
      setSessionFeedbackStatus('Unable to save feedback right now.');
    } finally {
      setFeedbackSendingFor(null);
    }
  };

  const handleSubmitInstruction = async (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    const trimmed = instructionDraft.trim();
    if (!trimmed) {
      setInstructionSaveStatus('Please enter an instruction.');
      return;
    }

    const scope = instructionScope;
    const accountId = scope === 'global' ? null : resolveAccountId();
    if (scope === 'account' && !accountId) {
      setInstructionSaveStatus('Account not found for scoped instructions.');
      return;
    }

    try {
      setIsSavingInstruction(true);
      setInstructionSaveStatus('Submitting...');
      const session = await createAssistantChatSession({
        sessionId: activeSessionId,
        title: 'Assistant instruction request',
        context: launchContext || null,
      });
      if (session) upsertSession(session);

      await createAssistantChatFeedback({
        sessionId: activeSessionId,
        comment: trimmed,
        metadata: {
          source: 'assistant-modal',
          feedbackType: 'instruction_request',
          instructionScope: scope,
          accountId: accountId || null,
          clientId: null,
          launchContext: launchContext || null,
        },
      });

      setInstructionDraft('');
      setInstructionSaveStatus('Submitted for review. n8n will process it into instructions.');
    } catch (error) {
      console.error('Failed to save instruction', error);
      setInstructionSaveStatus('Unable to submit instruction feedback.');
    } finally {
      setIsSavingInstruction(false);
    }
  };

  const handleClearInstructionDraft = () => {
    setInstructionDraft('');
    setInstructionSaveStatus('');
  };

  const handleStartInstructionEdit = (instruction: AiInstruction) => {
    setEditingInstructionId(instruction.id);
    setInstructionEditDraft(instruction.instruction || '');
    setInstructionActionStatus((prev) => ({ ...prev, [instruction.id]: '' }));
  };

  const handleCancelInstructionEdit = () => {
    setEditingInstructionId(null);
    setInstructionEditDraft('');
  };

  const handleSaveInstructionEdit = async (instruction: AiInstruction) => {
    const instructionId = instruction.id;
    const nextText = instructionEditDraft.trim();

    if (!nextText) {
      setInstructionActionStatus((prev) => ({
        ...prev,
        [instructionId]: 'Instruction cannot be empty.',
      }));
      return;
    }

    try {
      setIsUpdatingInstructionId(instructionId);
      setInstructionActionStatus((prev) => ({ ...prev, [instructionId]: 'Saving...' }));
      const updated = await updateAiInstruction({
        id: instructionId,
        instruction: nextText,
      });
      setInstructions((prev) => {
        const next = prev.map((item) => (item.id === instructionId ? { ...item, ...updated } : item));
        next.sort((a, b) => {
          const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
          return bTime - aTime;
        });
        return next;
      });
      setEditingInstructionId(null);
      setInstructionEditDraft('');
      setInstructionActionStatus((prev) => ({ ...prev, [instructionId]: 'Saved.' }));
    } catch (error) {
      console.error('Failed to update instruction', error);
      setInstructionActionStatus((prev) => ({
        ...prev,
        [instructionId]: 'Unable to save instruction.',
      }));
    } finally {
      setIsUpdatingInstructionId(null);
    }
  };

  const handleDeleteInstruction = async (instruction: AiInstruction) => {
    const instructionId = instruction.id;
    const confirmed = window.confirm('Delete this instruction?');
    if (!confirmed) return;

    try {
      setIsDeletingInstructionId(instructionId);
      setInstructionActionStatus((prev) => ({ ...prev, [instructionId]: 'Deleting...' }));
      await deleteAiInstruction(instructionId);
      setInstructions((prev) => prev.filter((item) => item.id !== instructionId));
      if (editingInstructionId === instructionId) {
        setEditingInstructionId(null);
        setInstructionEditDraft('');
      }
    } catch (error) {
      console.error('Failed to delete instruction', error);
      setInstructionActionStatus((prev) => ({
        ...prev,
        [instructionId]: 'Unable to delete instruction.',
      }));
    } finally {
      setIsDeletingInstructionId(null);
    }
  };

  const handleReviewFeedback = async (feedbackId: number, approved: boolean) => {
    const reviewComment = (reviewCommentDrafts[feedbackId] || '').trim();
    if (!reviewComment) {
      setReviewActionStatus((prev) => ({
        ...prev,
        [feedbackId]: 'Add a review comment before approving or rejecting.',
      }));
      return;
    }

    try {
      setFeedbackSendingFor(`review-${feedbackId}`);
      setReviewActionStatus((prev) => ({ ...prev, [feedbackId]: 'Saving review...' }));
      const updated = await reviewAssistantChatFeedback(feedbackId, approved, reviewComment);
      setReviewFeedbackItems((prev) =>
        prev.map((item) =>
          item.id === feedbackId
            ? {
                ...item,
                ...updated,
                message: item.message,
                session: item.session,
              }
            : item
        )
      );
      setReviewCommentDrafts((prev) => ({
        ...prev,
        [feedbackId]: updated.reviewComment || reviewComment,
      }));
      setReviewActionStatus((prev) => ({ ...prev, [feedbackId]: 'Saved.' }));
    } catch (error) {
      console.error('Failed to review feedback', error);
      setReviewActionStatus((prev) => ({
        ...prev,
        [feedbackId]: 'Unable to save review.',
      }));
    } finally {
      setFeedbackSendingFor(null);
    }
  };

  const handleOpenReviewConversation = (feedback: AssistantChatFeedback) => {
    if (!feedback?.sessionId) return;
    cancelActiveSend();
    const sessionId = feedback.sessionId;
    const sessionData =
      feedback.session ||
      ({
        id: 0,
        sessionId,
        title: 'Shared chat',
        createdAt: feedback.createdAt,
        updatedAt: feedback.createdAt,
      } as AssistantChatSession);

    upsertSession(sessionData);
    setMessageLoadScope('account');
    pendingHighlightIdRef.current = feedback.messageId ? Number(feedback.messageId) : null;
    lastLoadedSessionIdRef.current = null;
    setViewMode('chat');
    setActiveSessionId(sessionId);
  };

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed || isSending) return;

    const sessionId = activeSessionId;
    const userMessageId = `user-${Date.now()}`;
    const userMessage: AssistantMessage = {
      id: userMessageId,
      sender: 'user',
      text: trimmed,
      timestamp: formatTime(),
      sessionId,
    };

    lastLoadedSessionIdRef.current = sessionId;
    setMessages((prev) => [...prev, userMessage]);
    setDraft('');
    adjustInputHeight();
    resetStatusDisplay();
    setIsStreamingAssistantMessage(false);
    saveMessageBestEffort('user', trimmed, userMessageId, sessionId, null);

    if (!onSend) {
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-placeholder-${Date.now()}`,
          sender: 'assistant',
          text: "We'll pass this to QUIPA Assistant as soon as it is connected. For now, consider this a preview of the chat experience.",
          timestamp: formatTime(),
        },
      ]);
      return;
    }

    const requestController = new AbortController();
    sendAbortRef.current = requestController;
    sendStartedAtRef.current = Date.now();
    startSyntheticStatusSequence();
    let streamedAssistantId: string | null = null;
    let streamedAssistantText = '';

    const upsertStreamedAssistantMessage = (text: string, metadata: any = null) => {
      const nextText = text.trim();
      if (!nextText) return;

      streamedAssistantText = nextText;
      if (!streamedAssistantId) {
        streamedAssistantId = `assistant-stream-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
      }

      const localId = streamedAssistantId;
      const timestamp = formatTime();

      setMessages((prev) => {
        const existingIndex = prev.findIndex((item) => item.id === localId);
        const nextMessage: AssistantMessage = {
          id: localId,
          sender: 'assistant',
          text: nextText,
          timestamp,
          metadata,
          sessionId,
        };

        if (existingIndex === -1) {
          return [...prev, nextMessage];
        }

        const next = prev.slice();
        next[existingIndex] = {
          ...next[existingIndex],
          text: nextText,
          metadata: metadata ?? next[existingIndex].metadata ?? null,
          sessionId,
        };
        return next;
      });
    };

    const flushPendingStreamedMessage = (metadata: any = null) => {
      clearStreamedMessageFlush();
      const nextText = pendingStreamedMessageRef.current;
      if (!nextText) return;
      pendingStreamedMessageRef.current = '';
      upsertStreamedAssistantMessage(nextText, metadata);
    };

    const scheduleStreamedMessageFlush = () => {
      if (streamedMessageFlushRef.current != null) return;
      streamedMessageFlushRef.current = window.setTimeout(() => {
        streamedMessageFlushRef.current = null;
        flushPendingStreamedMessage();
      }, STREAM_MESSAGE_FLUSH_MS);
    };

    const streamCallbacks: AssistantSendCallbacks = {
      onMessage: (messageText) => {
        if (requestController.signal.aborted) return;
        resetStatusDisplay();
        setIsStreamingAssistantMessage(true);
        pendingStreamedMessageRef.current = messageText;
        scheduleStreamedMessageFlush();
      },
    };

    try {
      setIsSending(true);
      const reply = await onSend(trimmed, sessionId, streamCallbacks, requestController.signal);
      if (sendAbortRef.current === requestController) {
        sendAbortRef.current = null;
      }
      if (!reply) return;
      const assistantMessage: AssistantMessage =
        typeof reply === 'string'
          ? {
              id: `assistant-${Date.now()}`,
              sender: 'assistant',
              text: reply,
              timestamp: formatTime(),
              sessionId,
            }
          : {
              ...reply,
              sessionId: reply.sessionId || sessionId,
            };

      const wasStreamed = Boolean(
        typeof reply !== 'string' && assistantMessage.metadata?.wasStreamed
      );

      if (wasStreamed) {
        resetStatusDisplay();
        setIsStreamingAssistantMessage(false);

        if (assistantMessage.text) {
          pendingStreamedMessageRef.current = assistantMessage.text;
          flushPendingStreamedMessage(assistantMessage.metadata ?? null);
        }

        if (!streamedAssistantId && assistantMessage.text) {
          streamedAssistantId = assistantMessage.id;
          setMessages((prev) => [...prev, assistantMessage]);
        }

        if (streamedAssistantId && streamedAssistantText) {
          saveMessageBestEffort(
            'assistant',
            streamedAssistantText,
            streamedAssistantId,
            sessionId,
            assistantMessage.metadata ?? null
          );
        }
        return;
      }

      setMessages((prev) => [...prev, assistantMessage]);
      saveMessageBestEffort(
        'assistant',
        assistantMessage.text,
        assistantMessage.id,
        sessionId,
        assistantMessage.metadata ?? null
      );
    } catch (error) {
      if (requestController.signal.aborted) return;
      resetStatusDisplay();
      setIsStreamingAssistantMessage(false);
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-error-${Date.now()}`,
          sender: 'assistant',
          text: 'We could not reach the assistant. Please try again in a moment.',
          timestamp: formatTime(),
        },
      ]);
    } finally {
      flushPendingStreamedMessage();
      if (sendAbortRef.current === requestController) {
        sendAbortRef.current = null;
      }
      resetStatusDisplay();
      setIsStreamingAssistantMessage(false);
      setIsSending(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <PageDimmer uiActive={isOpen} />
      <AssistantModalContainer
        uiActive={isOpen}
        uiOnRequestClose={onClose}
        uiDefaultSizes={false}
        uiCloseOnEsc
        uiCloseOnOutsideClick
      >
        <AssistantShell>
          <Header>
            <HeaderTitle>Create Project with QUIPA Assistant</HeaderTitle>
            <HeaderRight>
              {canReviewFeedback && (
                <HeaderActionButton
                  type="button"
                  isActive={viewMode === 'review'}
                  onClick={() => setViewMode(viewMode === 'review' ? 'chat' : 'review')}
                >
                  {viewMode === 'review' ? 'Back to chat' : 'Review feedback'}
                </HeaderActionButton>
              )}
              {canManageInstructions && (
                <HeaderActionButton
                  type="button"
                  isActive={viewMode === 'instructions'}
                  onClick={() => setViewMode(viewMode === 'instructions' ? 'chat' : 'instructions')}
                >
                  {viewMode === 'instructions' ? 'Back to chat' : 'Manage instructions'}
                </HeaderActionButton>
              )}
              <Pill>QUIPA Assistant Preview</Pill>
              <CloseButton type="button" aria-label="Close assistant" onClick={onClose}>
                <FiX />
              </CloseButton>
            </HeaderRight>
          </Header>

          <Body>
            {viewMode === 'chat' && (
              <Sidebar>
                <SidebarHeader>
                  <SidebarTitle>Chats</SidebarTitle>
                  <NewChatButton
                    type="button"
                    onClick={handleNewChat}
                    aria-label="Start a new chat"
                  >
                    <FiPlus /> New chat
                  </NewChatButton>
                </SidebarHeader>
                <SessionsList>
                  {isLoadingSessions && <SessionMeta>Loading chats...</SessionMeta>}
                  {!isLoadingSessions && sessions.length === 0 && (
                    <SessionMeta>No previous chats yet.</SessionMeta>
                  )}
                  {sessions.map((session) => {
                    const isActive = session.sessionId === activeSessionId;
                    const title = session.title || 'Untitled chat';
                    const updatedAt = session.updatedAt || session.createdAt;
                    const meta = updatedAt ? moment(updatedAt).format('MMM D, YYYY') : '';
                    return (
                      <SessionButton
                        key={session.sessionId}
                        type="button"
                        isActive={isActive}
                        onClick={() => handleSelectSession(session)}
                      >
                        <SessionTitle>{title}</SessionTitle>
                        <SessionMeta>{meta}</SessionMeta>
                      </SessionButton>
                    );
                  })}
                </SessionsList>
              </Sidebar>
            )}

            <ChatSurface>
              {viewMode === 'review' && canReviewFeedback ? (
                <ReviewContainer>
                  <ReviewHeader>
                    <ReviewTitle>Feedback Review</ReviewTitle>
                    <ReviewSubtitle>
                      Approve or reject feedback before it is processed for training.
                    </ReviewSubtitle>
                  </ReviewHeader>
                  <ReviewList>
                    {isLoadingReviewFeedback && <SessionMeta>Loading feedback...</SessionMeta>}
                    {!isLoadingReviewFeedback && reviewFeedbackItems.length === 0 && (
                      <SessionMeta>No feedback submitted yet.</SessionMeta>
                    )}
                    {reviewFeedbackItems.map((feedback) => {
                      const feedbackUserName = [feedback.user?.firstName, feedback.user?.lastName]
                        .filter(Boolean)
                        .join(' ')
                        .trim();
                      const feedbackUserEmail = feedback.user?.email || '';
                      const feedbackUserLabel =
                        feedbackUserName && feedbackUserEmail
                          ? `${feedbackUserName} · ${feedbackUserEmail}`
                          : feedbackUserName ||
                            feedbackUserEmail ||
                            (feedback.userId ? `User #${feedback.userId}` : 'Unknown user');
                      const helpfulTag =
                        feedback.isHelpful === true
                          ? 'User marked: Helpful'
                          : feedback.isHelpful === false
                          ? 'User marked: Not Helpful'
                          : 'User did not rate';
                      const sessionTitle =
                        feedback.session?.title || `Session ${feedback.sessionId}`;
                      const messageText = feedback.message?.text || '';
                      const messagePreview =
                        messageText.length > 220 ? `${messageText.slice(0, 217)}...` : messageText;
                      const reviewCommentDraft =
                        reviewCommentDrafts[feedback.id] ?? feedback.reviewComment ?? '';
                      const reviewReady = Boolean(reviewCommentDraft.trim());
                      return (
                        <ReviewCard key={`feedback-${feedback.id}`}>
                          <ReviewTagRow>
                            <ReviewTag>{helpfulTag}</ReviewTag>
                            <ReviewTag>{sessionTitle}</ReviewTag>
                          </ReviewTagRow>
                          <ReviewMeta>Submitted by: {feedbackUserLabel}</ReviewMeta>
                          <ReviewField>
                            <ReviewFieldLabel>User feedback</ReviewFieldLabel>
                            <ReviewComment>{feedback.comment}</ReviewComment>
                          </ReviewField>
                          {messagePreview && <ReviewExcerpt>{messagePreview}</ReviewExcerpt>}
                          <ReviewField>
                            <ReviewFieldLabel>Review notes</ReviewFieldLabel>
                            <ReviewTextarea
                              value={reviewCommentDraft}
                              onChange={(event) => {
                                const value = event.target.value;
                                setReviewCommentDrafts((prev) => ({
                                  ...prev,
                                  [feedback.id]: value,
                                }));
                                setReviewActionStatus((prev) => ({
                                  ...prev,
                                  [feedback.id]: '',
                                }));
                              }}
                              placeholder="Add context for training or why this should be rejected."
                            />
                          </ReviewField>
                          <ReviewOpenRow>
                            <ReviewOpenButton
                              type="button"
                              onClick={() => handleOpenReviewConversation(feedback)}
                            >
                              Open chat
                            </ReviewOpenButton>
                          </ReviewOpenRow>
                          <ReviewActions>
                            <ReviewMeta>
                              {feedback.isUseful === true
                                ? 'Approved for training'
                                : feedback.isUseful === false
                                ? 'Rejected for training'
                                : 'Not reviewed yet'}
                            </ReviewMeta>
                            <ReviewButtons>
                              <ReviewButton
                                type="button"
                                intent="up"
                                active={feedback.isUseful === true}
                                disabled={
                                  feedbackSendingFor === `review-${feedback.id}` || !reviewReady
                                }
                                onClick={() => handleReviewFeedback(feedback.id, true)}
                              >
                                <FiThumbsUp /> Approve
                              </ReviewButton>
                              <ReviewButton
                                type="button"
                                intent="down"
                                active={feedback.isUseful === false}
                                disabled={
                                  feedbackSendingFor === `review-${feedback.id}` || !reviewReady
                                }
                                onClick={() => handleReviewFeedback(feedback.id, false)}
                              >
                                <FiThumbsDown /> Reject
                              </ReviewButton>
                            </ReviewButtons>
                          </ReviewActions>
                          {reviewActionStatus[feedback.id] && (
                            <ReviewStatus>{reviewActionStatus[feedback.id]}</ReviewStatus>
                          )}
                        </ReviewCard>
                      );
                    })}
                  </ReviewList>
                </ReviewContainer>
              ) : viewMode === 'instructions' && canManageInstructions ? (
                <InstructionsContainer>
                  <InstructionsHeader>
                    <InstructionsTitle>Assistant Instructions</InstructionsTitle>
                    <InstructionsSubtitle>
                      Submit global or account instruction feedback. Approved feedback is processed
                      into instructions by n8n.
                    </InstructionsSubtitle>
                  </InstructionsHeader>
                  <InstructionsBody>
                    <InstructionsField>
                      <InstructionsLabel>View instructions</InstructionsLabel>
                      <InstructionsRow>
                        <InstructionsSelect
                          value={instructionFilterScope}
                          onChange={(event) => {
                            const value = event.target.value as InstructionViewScope;
                            setInstructionFilterScope(value);
                            setInstructionSaveStatus('');
                          }}
                        >
                          <option value="all">All</option>
                          <option value="global">Global</option>
                          <option value="account">Account</option>
                        </InstructionsSelect>
                      </InstructionsRow>
                    </InstructionsField>
                    <InstructionsForm onSubmit={handleSubmitInstruction}>
                      <InstructionsField>
                        <InstructionsLabel>New instruction scope</InstructionsLabel>
                        <InstructionsRow>
                          <InstructionsSelect
                            value={instructionScope}
                            onChange={(event) => {
                              const value = event.target.value as NewInstructionScope;
                              setInstructionScope(value);
                              setInstructionSaveStatus('');
                            }}
                          >
                            <option value="global">Global</option>
                            <option value="account">Account</option>
                          </InstructionsSelect>
                          {instructionScope === 'account' && (
                            <InstructionsHint>
                              Account ID: {instructionAccountId || 'Unavailable'}
                            </InstructionsHint>
                          )}
                        </InstructionsRow>
                      </InstructionsField>
                      <InstructionsField>
                        <InstructionsLabel>Instruction</InstructionsLabel>
                        <InstructionsTextarea
                          value={instructionDraft}
                          onChange={(event) => {
                            setInstructionDraft(event.target.value);
                            if (instructionSaveStatus) setInstructionSaveStatus('');
                          }}
                          placeholder="Add a directive for the assistant..."
                        />
                      </InstructionsField>
                      <InstructionsActions>
                        <FeedbackStatus>{instructionSaveStatus}</FeedbackStatus>
                        <InstructionsButtons>
                          <InstructionsGhostButton
                            type="button"
                            onClick={handleClearInstructionDraft}
                          >
                            Clear
                          </InstructionsGhostButton>
                          <InstructionsSubmitButton type="submit" disabled={isSavingInstruction}>
                            {isSavingInstruction ? 'Submitting...' : 'Submit instruction'}
                          </InstructionsSubmitButton>
                        </InstructionsButtons>
                      </InstructionsActions>
                    </InstructionsForm>
                    <InstructionsList>
                      {isLoadingInstructions && (
                        <SessionMeta>Loading instructions...</SessionMeta>
                      )}
                      {!isLoadingInstructions && instructions.length === 0 && (
                        <SessionMeta>No instructions saved yet.</SessionMeta>
                      )}
                      {instructions.map((instruction) => {
                        const scope =
                          instruction.scope ||
                          (!instruction.accountId
                            ? 'global'
                            : instruction.clientId
                            ? 'client'
                            : 'account');
                        const createdAt = instruction.createdAt || instruction.updatedAt;
                        const createdLabel = createdAt
                          ? moment(createdAt).format('MMM D, YYYY')
                          : '';
                        const statusLabel = instruction.status ? String(instruction.status) : '';
                        const isEditing = editingInstructionId === instruction.id;
                        const actionStatus = instructionActionStatus[instruction.id];
                        const isSavingThis = isUpdatingInstructionId === instruction.id;
                        const isDeletingThis = isDeletingInstructionId === instruction.id;
                        return (
                          <InstructionCard key={`instruction-${instruction.id}`}>
                            <InstructionMetaRow>
                              <InstructionMetaPill>{scope}</InstructionMetaPill>
                              {statusLabel && <InstructionMetaPill>{statusLabel}</InstructionMetaPill>}
                              {instruction.category && (
                                <InstructionMetaPill>{instruction.category}</InstructionMetaPill>
                              )}
                              {(scope === 'account' || scope === 'client') && instruction.accountId && (
                                <InstructionMetaPill>
                                  Account #{instruction.accountId}
                                </InstructionMetaPill>
                              )}
                              {scope === 'client' && instruction.clientId && (
                                <InstructionMetaPill>Client #{instruction.clientId}</InstructionMetaPill>
                              )}
                              {createdLabel && <InstructionMetaPill>{createdLabel}</InstructionMetaPill>}
                            </InstructionMetaRow>
                            {isEditing ? (
                              <InstructionEditTextarea
                                value={instructionEditDraft}
                                onChange={(event) => setInstructionEditDraft(event.target.value)}
                              />
                            ) : (
                              <InstructionText>{instruction.instruction}</InstructionText>
                            )}
                            <InstructionCardActions>
                              {isEditing ? (
                                <>
                                  <InstructionActionButton
                                    type="button"
                                    intent="primary"
                                    disabled={isSavingThis || isDeletingThis}
                                    onClick={() => handleSaveInstructionEdit(instruction)}
                                  >
                                    {isSavingThis ? 'Saving...' : 'Save'}
                                  </InstructionActionButton>
                                  <InstructionActionButton
                                    type="button"
                                    intent="ghost"
                                    disabled={isSavingThis || isDeletingThis}
                                    onClick={handleCancelInstructionEdit}
                                  >
                                    Cancel
                                  </InstructionActionButton>
                                </>
                              ) : (
                                <InstructionActionButton
                                  type="button"
                                  intent="ghost"
                                  disabled={isSavingThis || isDeletingThis}
                                  onClick={() => handleStartInstructionEdit(instruction)}
                                >
                                  Edit
                                </InstructionActionButton>
                              )}
                              <InstructionActionButton
                                type="button"
                                intent="danger"
                                disabled={isSavingThis || isDeletingThis}
                                onClick={() => handleDeleteInstruction(instruction)}
                              >
                                {isDeletingThis ? 'Deleting...' : 'Delete'}
                              </InstructionActionButton>
                            </InstructionCardActions>
                            {actionStatus && <InstructionActionStatus>{actionStatus}</InstructionActionStatus>}
                          </InstructionCard>
                        );
                      })}
                    </InstructionsList>
                  </InstructionsBody>
                </InstructionsContainer>
              ) : (
                <>
                  <MessageList ref={listRef}>
                    {isLoadingMessages && <SessionMeta>Loading messages...</SessionMeta>}
                    {!isLoadingMessages && messages.length === 0 && (
                      <EmptyState>
                        <EmptyStateContent>
                          <EmptyStateTitle>Where should we begin?</EmptyStateTitle>
                          <EmptyStateSubtitle>
                            Share the project details or paste your designer&apos;s email to get
                            started.
                          </EmptyStateSubtitle>
                        </EmptyStateContent>
                      </EmptyState>
                    )}
                    {isLoadingSessionFeedback && (
                      <SessionMeta>Loading feedback...</SessionMeta>
                    )}
                    {!isLoadingSessionFeedback && sessionLevelFeedback.length > 0 && (
                      <>
                        <SessionFeedbackBanner>
                          <span>Chat feedback received ({sessionLevelFeedback.length})</span>
                          <FeedbackCountButton
                            type="button"
                            onClick={() => setSessionFeedbackViewOpen((prev) => !prev)}
                          >
                            {sessionFeedbackViewOpen ? 'Hide feedback' : 'View feedback'}
                          </FeedbackCountButton>
                        </SessionFeedbackBanner>
                        {sessionFeedbackViewOpen && (
                          <FeedbackInlinePanel>
                            <FeedbackInlineHeader>Chat feedback</FeedbackInlineHeader>
                            {sessionLevelFeedback.map((feedback) => {
                              const helpfulLabel =
                                feedback.isHelpful === true
                                  ? 'Helpful'
                                  : feedback.isHelpful === false
                                  ? 'Not Helpful'
                                  : 'No rating';
                              return (
                                <FeedbackInlineItem key={`session-feedback-${feedback.id}`}>
                                  <FeedbackInlineMeta>{helpfulLabel}</FeedbackInlineMeta>
                                  <FeedbackInlineComment>{feedback.comment}</FeedbackInlineComment>
                                </FeedbackInlineItem>
                              );
                            })}
                          </FeedbackInlinePanel>
                        )}
                      </>
                    )}
                    {messages.map((message) => {
                      const persistedId = message.persistedId ? Number(message.persistedId) : null;
                      const messageFeedback = persistedId
                        ? messageFeedbackMap.get(persistedId) || []
                        : [];
                      const showMessageFeedback =
                        persistedId &&
                        messageFeedback.length > 0 &&
                        (messageFeedbackViewOpen[persistedId] ||
                          highlightedMessageId === persistedId);
                      const isHighlighted = Boolean(
                        persistedId && highlightedMessageId === persistedId
                      );
                      return (
                        <MessageRow
                          key={message.id}
                          fromUser={message.sender === 'user'}
                          data-message-id={persistedId || undefined}
                        >
                          <MessageStack fromUser={message.sender === 'user'}>
                            <MessageBubble
                              fromUser={message.sender === 'user'}
                              isHighlighted={isHighlighted}
                            >
                              {message.sender === 'user' ? (
                                <PlainText>{message.text}</PlainText>
                              ) : (
                                <AssistantMarkdown text={message.text} />
                              )}
                            </MessageBubble>
                            {message.sender === 'assistant' && (
                              <>
                                <MessageActions fromUser={message.sender === 'user'}>
                                  {messageFeedback.length > 0 && persistedId && (
                                    <FeedbackCountButton
                                      type="button"
                                      onClick={() =>
                                        setMessageFeedbackViewOpen((prev) => ({
                                          ...prev,
                                          [persistedId]: !prev[persistedId],
                                        }))
                                      }
                                    >
                                      {messageFeedbackViewOpen[persistedId]
                                        ? `Hide feedback (${messageFeedback.length})`
                                        : `View feedback (${messageFeedback.length})`}
                                    </FeedbackCountButton>
                                  )}
                                  <FeedbackLinkButton
                                    type="button"
                                    disabled={!message.persistedId}
                                    onClick={() => toggleMessageFeedback(message.id)}
                                  >
                                    {message.persistedId
                                      ? messageFeedbackOpen[message.id]
                                        ? 'Hide feedback'
                                        : 'Leave feedback'
                                      : 'Saving...'}
                                  </FeedbackLinkButton>
                                </MessageActions>
                                {showMessageFeedback && (
                                  <FeedbackInlinePanel>
                                    <FeedbackInlineHeader>
                                      Feedback on this message ({messageFeedback.length})
                                    </FeedbackInlineHeader>
                                    {messageFeedback.map((feedback) => {
                                      const helpfulLabel =
                                        feedback.isHelpful === true
                                          ? 'Helpful'
                                          : feedback.isHelpful === false
                                          ? 'Not Helpful'
                                          : 'No rating';
                                      return (
                                        <FeedbackInlineItem
                                          key={`message-feedback-${feedback.id}`}
                                        >
                                          <FeedbackInlineMeta>{helpfulLabel}</FeedbackInlineMeta>
                                          <FeedbackInlineComment>
                                            {feedback.comment}
                                          </FeedbackInlineComment>
                                        </FeedbackInlineItem>
                                      );
                                    })}
                                  </FeedbackInlinePanel>
                                )}
                                {messageFeedbackOpen[message.id] && (
                                  <FeedbackPanel>
                                    <FeedbackLabel>What should the assistant have said?</FeedbackLabel>
                                    <FeedbackRatingGroup>
                                      <FeedbackRatingButton
                                        type="button"
                                        intent="good"
                                        active={messageFeedbackRatings[message.id] === true}
                                        onClick={() =>
                                          setMessageFeedbackRatings((prev) => ({
                                            ...prev,
                                            [message.id]: true,
                                          }))
                                        }
                                      >
                                        Helpful
                                      </FeedbackRatingButton>
                                      <FeedbackRatingButton
                                        type="button"
                                        intent="bad"
                                        active={messageFeedbackRatings[message.id] === false}
                                        onClick={() =>
                                          setMessageFeedbackRatings((prev) => ({
                                            ...prev,
                                            [message.id]: false,
                                          }))
                                        }
                                      >
                                        Not Helpful
                                      </FeedbackRatingButton>
                                    </FeedbackRatingGroup>
                                    <FeedbackTextarea
                                      value={messageFeedbackDrafts[message.id] || ''}
                                      onChange={(event) => {
                                        const value = event.target.value;
                                        setMessageFeedbackDrafts((prev) => ({
                                          ...prev,
                                          [message.id]: value,
                                        }));
                                      }}
                                      placeholder="Share the correct response and why."
                                    />
                                    <FeedbackActions>
                                      <FeedbackStatus>{messageFeedbackStatus[message.id] || ''}</FeedbackStatus>
                                      <FeedbackButtons>
                                        <FeedbackGhostButton
                                          type="button"
                                          onClick={() =>
                                            setMessageFeedbackOpen((prev) => ({
                                              ...prev,
                                              [message.id]: false,
                                            }))
                                          }
                                        >
                                          Cancel
                                        </FeedbackGhostButton>
                                        <FeedbackSubmitButton
                                          type="button"
                                          disabled={
                                            feedbackSendingFor === message.id ||
                                            !(messageFeedbackDrafts[message.id] || '').trim() ||
                                            typeof messageFeedbackRatings[message.id] !== 'boolean'
                                          }
                                          onClick={() => handleSubmitMessageFeedback(message)}
                                        >
                                          Submit
                                        </FeedbackSubmitButton>
                                      </FeedbackButtons>
                                    </FeedbackActions>
                                  </FeedbackPanel>
                                )}
                              </>
                            )}
                            <Timestamp fromUser={message.sender === 'user'}>
                              {message.timestamp}
                            </Timestamp>
                          </MessageStack>
                        </MessageRow>
                      );
                    })}
                  </MessageList>

                  {isSending && !isStreamingAssistantMessage && (
                    <ThinkingBar>
                      <Dot />
                      <Dot delay="0.15s" />
                      <Dot delay="0.3s" />
                      <span>{assistantStatus || 'Assistant is thinking...'}</span>
                    </ThinkingBar>
                  )}

                  <Composer
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleSend();
                    }}
                  >
                    <ComposerRow>
                      <PromptInput
                        ref={inputRef}
                        value={draft}
                        onChange={(event) => {
                          setDraft(event.target.value);
                        }}
                        placeholder="Type your response here, or paste your designer's email..."
                        onKeyDown={handleKeyDown}
                        rows={3}
                      />
                      <SendButton
                        type="submit"
                        disabled={!draft.trim() || isSending}
                        aria-label="Send"
                      >
                        <FiSend />
                      </SendButton>
                    </ComposerRow>
                    <ShortcutHint>Press Enter to send, Shift+Enter for new line</ShortcutHint>
                    <SessionFeedbackSection>
                      <SessionFeedbackToggle
                        type="button"
                        onClick={() => setSessionFeedbackOpen((prev) => !prev)}
                      >
                        {sessionFeedbackOpen
                          ? 'Hide chat feedback'
                          : 'Leave feedback about this chat'}
                      </SessionFeedbackToggle>
                      {sessionFeedbackOpen && (
                        <FeedbackPanel>
                          <FeedbackLabel>What should the assistant have said?</FeedbackLabel>
                          <FeedbackRatingGroup>
                            <FeedbackRatingButton
                              type="button"
                              intent="good"
                              active={sessionFeedbackRating === true}
                              onClick={() => setSessionFeedbackRating(true)}
                            >
                              Helpful
                            </FeedbackRatingButton>
                            <FeedbackRatingButton
                              type="button"
                              intent="bad"
                              active={sessionFeedbackRating === false}
                              onClick={() => setSessionFeedbackRating(false)}
                            >
                              Not Helpful
                            </FeedbackRatingButton>
                          </FeedbackRatingGroup>
                          <FeedbackTextarea
                            value={sessionFeedbackDraft}
                            onChange={(event) => setSessionFeedbackDraft(event.target.value)}
                            placeholder="Share the correct response and why."
                          />
                          <FeedbackActions>
                            <FeedbackStatus>{sessionFeedbackStatus || ''}</FeedbackStatus>
                            <FeedbackButtons>
                              <FeedbackGhostButton
                                type="button"
                                onClick={() => {
                                  setSessionFeedbackOpen(false);
                                  setSessionFeedbackStatus('');
                                }}
                              >
                                Cancel
                              </FeedbackGhostButton>
                              <FeedbackSubmitButton
                                type="button"
                                disabled={
                                  feedbackSendingFor === 'session' ||
                                  !sessionFeedbackDraft.trim() ||
                                  typeof sessionFeedbackRating !== 'boolean'
                                }
                                onClick={handleSubmitSessionFeedback}
                              >
                                Submit
                              </FeedbackSubmitButton>
                            </FeedbackButtons>
                          </FeedbackActions>
                        </FeedbackPanel>
                      )}
                    </SessionFeedbackSection>
                  </Composer>
                </>
              )}
            </ChatSurface>
          </Body>
        </AssistantShell>
      </AssistantModalContainer>
    </>
  );
};

export default QuipaAssistantModal;
